import { useQuery } from '@apollo/client';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { ResolvedEvent } from '@thro-types/events';

import { useFormikContext } from 'formik';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import USERS_BY_EMAIL_QUERY from 'gql/queries/EventService/getUsersEmail.gql';

import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';

interface UserOption {
  id: number;
  email: string;
}

export const UserAutocomplete = ({ eventData }: { eventData: ResolvedEvent }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);

  const { setFieldValue } = useFormikContext();

  const isRegPeriodOver = eventData.registrationPeriodEnd < new Date();

  const getUsersQuery = useQuery<
    { users: UserOption[] },
    { filter?: string; type?: UserType; entitlements?: string[]; limit?: number }
  >(USERS_BY_EMAIL_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      type: UserType.SHIBBOLETH,
      entitlements: isRegPeriodOver ? undefined : eventData.participantCriteria?.entitlements,
      limit: 10
    }
  });

  useEffect(() => {
    setLoading(getUsersQuery.loading);
    if (!!getUsersQuery.data) {
      setUserOptions([...getUsersQuery.data?.users]);
    }
  }, [getUsersQuery]);

  const changeHandler = value => {
    if (value !== '') {
      const filter: string = JSON.stringify({
        email: { term: value }
      });
      getUsersQuery.refetch({
        filter: filter,
        type: UserType.SHIBBOLETH,
        entitlements: isRegPeriodOver ? undefined : eventData.participantCriteria?.entitlements,
        limit: 10
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceChangeHandler = useCallback(
    debounce(value => {
      changeHandler(value);
    }, 500),
    []
  );

  const handleChange = (event, value: UserOption | null) => {
    setFieldValue('userId', value?.id);
  };

  return (
    <Autocomplete
      defaultValue={null}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      onInputChange={(event, value) => {
        debounceChangeHandler(value);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={option => option.email}
      onChange={handleChange}
      options={userOptions}
      loading={loading}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={t(translations.pages.events.create.organizingPersonsPlaceholder())}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            )
          }}
        />
      )}
    />
  );
};
